import React, { useState } from "react";

import { Link, useNavigate, useSearchParams } from "react-router-dom";

import Footer from "./Shared/Footer";
import LoadingBar from "react-top-loading-bar";
import { BsChevronRight } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { handleFetchProducts } from "../utils/api";
import Stars from "./Shared/Stars";
import MetaTags from "./Shared/MetaTags";

var currency_state = { code: "GBP", name: "British Sterling", symbol: "£" };
var language_state = "en";
var zone_state = "UK";

const Shop = () => {
  const [search] = useSearchParams();
  const [language, setLanguage] = useState(language_state);
  const [currency, setCurrency] = useState(currency_state);
  const [zone, setZone] = useState(zone_state);
  const [progress, setProgress] = useState(0);

  const { t } = useTranslation();

  window.addEventListener("located", () => {
    var user_prefs = JSON.parse(localStorage.getItem("user-prefsv2"));
    setLanguage(user_prefs.language);
    setCurrency(user_prefs.currency);
    setZone(user_prefs.customer_zone);
  });

  React.useEffect(() => {
    if (localStorage.getItem("user-localev2") != null) {
      let user_prefs = JSON.parse(localStorage.getItem("user-prefsv2"));
      setCurrency(user_prefs.currency);
      setLanguage(user_prefs.language);
      setZone(user_prefs.customer_zone);
    }
  }, []);

  const products = useQuery({
    queryKey: ["products", { wired: search.get("wired") }],
    queryFn: handleFetchProducts,
    refetchOnWindowFocus: false,
    refetchOnTabFocus: false,
    onSuccess: () => {
      setProgress(100);
    },
    retry: false,
  });

  return (
    <div className="w-full min-h-screen flex flex-col">
      <MetaTags
        title={t("shop.helmet.title")}
        description={t("shop.helmet.desc")}
      />

      <LoadingBar
        color="#AB4843"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />
      <div className="w-full text-center bg-pr p-2 text-white font-semibold text-sm mt-2 md:mt-1 lg:mt-0">
        {zone === "UK" ? t("shop.banner.uk") : t("shop.banner.int")}
      </div>

      <div className="flex w-full justify-center">
        <div className="px-3 max-w-5xl w-full px-auto text-sm py-4 flex flex-row gap-2 font-semibold items-center ">
          <Link to="/">{t("shop.link.home")}</Link> <BsChevronRight />{" "}
          <Link className="text-pr" to="/shop">
            {t("shop.link.shop")}
          </Link>
        </div>
      </div>

      <main className="px-3 flex-1">
        <div className="max-w-5xl mx-auto">
          <h1 className="mt-5 text-4xl md:text-5xl font-bold">
            {t("shop.products.title")}
          </h1>
          <p className="text-lg max-w-xl text-gray-500 mt-4">
            {t("shop.products.desc")}
          </p>
        </div>

        {products.isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto mt-8">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="flex flex-col justify-center items-center p-4 bg-white rounded-md shadow-lg"
              >
                <div className="w-full h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                <div className="w-full h-32 bg-gray-300 rounded-lg animate-pulse mt-4"></div>
                <div className="w-full h-8 bg-gray-300 rounded-lg animate-pulse mt-4"></div>
                <div className="w-full h-24 bg-gray-300 rounded-lg animate-pulse mt-4"></div>
              </div>
            ))}
          </div>
        ) : products.isError ? (
          <div>
            <img
              className="mx-auto my-8 h-48"
              src="/assets/shopPage/engineer.svg"
              alt="engineer"
            />
            <p className="text-xs font-bold max-w-lg text-left mx-auto">
              {t("shop.products.error_message")}
            </p>

            <div className="flex flex-row gap-4 justify-center w-full mt-6">
              <a href="https://www.amazon.co.uk/safetag">
                <button className="h-10 p-2 px-6 border outline-none rounded shadow">
                  <img
                    className="h-full"
                    src="/assets/shopPage/amazon.svg"
                    alt="amazon"
                  />
                </button>
              </a>
              <a href="https://www.ebay.co.uk/sch/safetagdirect/m.html?item=314181167467&rt=nc&_trksid=p2047675.m3561.l2562">
                <button className="h-10 p-2 px-6 border outline-none rounded shadow">
                  <img
                    className="h-full"
                    src="/assets/shopPage/ebay.svg"
                    alt="eBay"
                  />
                </button>
              </a>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto mt-8">
            {products.data.map((product, i) => (
              <Link
                to={`/product/${product.name}`}
                className="flex flex-col p-4 bg-white rounded-xl border hover:border-pr"
                style={{ boxShadow: "0px 0px 20px #4444" }}
                key={i}
              >
                <h1 className="text-left font-bold text-xl">
                  {product.display.name[language]
                    ? product.display.name[language]
                    : product.display.name.en}
                </h1>
                <div className="px-4 py-8 w-full">
                  <img
                    style={{ textShadow: "2px 2px 8px #000000" }}
                    className="h-44  mx-auto -my-5"
                    src={product.images[0]}
                    alt={product.display.name.en}
                  />
                </div>

                <div className="flex flex-row items-center gap-2 justify-end text-sm">
                  <Stars stars={getAverageRating(product.display.reviews)} />
                  <div>({product.display.reviews.en.length})</div>
                </div>

                {product.prices ? (
                  <div className="flex flex-row gap-2 items-center">
                    <div className="text-pr font-bold text-xl">
                      {currency.symbol}{" "}
                      {(
                        product.prices[zone].single[
                          currency.code.toLowerCase()
                        ] / 100
                      ).toFixed(2)}
                    </div>
                    <div className="flex-1 border-0 border-t-2 border-gray-500"></div>
                  </div>
                ) : (
                  <div className="flex flex-row gap-2 items-center">
                    <Link
                      to="/contact?reason=business"
                      className="text-pr font-bold text-lg hover:text-sr transition-all duration-300"
                    >
                      {t("confirm_email.main.contact")}
                    </Link>
                    <div className="flex-1 border-0 border-t-2 border-gray-500"></div>
                  </div>
                )}

                <div className="p-4 leading-5 mt-4 rounded-lg text-xs bg-pr bg-opacity-10 flex-1 font-semibold ">
                  {product.display.desc[language]
                    ? product.display.desc[language]
                    : product.display.desc.en}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <div className="pt-10">
        <Footer />
      </div>
    </div>
  );
};

const getAverageRating = (reviews) => {
  let total = 0;
  let count = 0;
  reviews.en.forEach(({ stars }) => {
    total += stars;
    count++;
  });
  return Math.round(total / count);
};

export default Shop;
